import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { TextInput, Textarea, Select, NumberInput, Switch, Button, Title, Stack, Group, SegmentedControl, Text, SimpleGrid } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import ImageUpload from '@/components/ImageUpload'
import { api } from '@/lib/api'
import { formatName } from '@/lib/format'

const statuses = [
  { value: 'draft', label: 'Ноорог' },
  { value: 'published', label: 'Нийтлэгдсэн' },
]

const premiumDurationOptions = [
  { value: '1', label: '1 сар' },
  { value: '3', label: '3 сар' },
  { value: '6', label: '6 сар' },
  { value: '12', label: '1 жил' },
]

export default function CourseForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [teachers, setTeachers] = useState<{ value: string; label: string }[]>([])
  const [categories, setCategories] = useState<string[]>([])

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      teacherId: '',
      priceType: 'free' as 'free' | 'premium' | 'fixed',
      price: 0,
      premiumDuration: '1',
      category: 'Философи',
      status: 'draft',
      isFeatured: false,
      isMemberOnly: false,
      thumbnailUrl: '',
      totalHours: 0,
    },
  })

  useEffect(() => {
    api.admin.getTeachers({ limit: '100' }).then((res) => {
      setTeachers(
        res.data.map((t) => {
          const user = t.userId as Record<string, unknown> | undefined
          return {
            value: t._id as string,
            label: formatName(user?.fullName as string) || 'Unknown',
          }
        })
      )
    })
    api.admin.getCategories().then((res) => {
      setCategories(res.courseCategories)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (id) {
      fetch(`${import.meta.env.VITE_API_URL || '/api/v1'}/courses/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      })
        .then((r) => r.json())
        .then((course) => {
          const teacherId = typeof course.teacherId === 'object' ? course.teacherId._id : course.teacherId
          form.setValues({
            title: course.title || '',
            description: course.description || '',
            teacherId: teacherId || '',
            priceType: course.priceType || (course.price > 0 ? 'fixed' : 'free'),
            price: course.price || 0,
            premiumDuration: String(course.premiumDuration || 1),
            category: course.category || 'Философи',
            status: course.status || 'draft',
            isFeatured: course.isFeatured || false,
            isMemberOnly: course.isMemberOnly || false,
            thumbnailUrl: course.thumbnailUrl || '',
            totalHours: course.totalHours || 0,
          })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true)
    try {
      const payload = {
        title: values.title,
        description: values.description,
        teacherId: values.teacherId,
        priceType: values.priceType,
        price: values.priceType === 'fixed' ? values.price : 0,
        premiumDuration: values.priceType === 'premium' ? Number(values.premiumDuration) : null,
        category: values.category,
        status: values.status,
        isFeatured: values.isFeatured,
        isMemberOnly: values.isMemberOnly,
        thumbnailUrl: values.thumbnailUrl,
        totalHours: values.totalHours,
      }
      if (id) {
        await api.admin.updateCourse(id, payload)
        notifications.show({ title: 'Амжилттай', message: 'Сургалт шинэчлэгдлээ', color: 'green' })
      } else {
        await api.admin.createCourse(payload)
        notifications.show({ title: 'Амжилттай', message: 'Сургалт үүсгэгдлээ', color: 'green' })
      }
      navigate('/courses')
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Алдаа гарлаа', color: 'red' })
    } finally {
      setLoading(false)
    }
  }

  const priceType = form.values.priceType

  return (
    <div>
      <Title order={2} mb="lg">{id ? 'Сургалт засах' : 'Шинэ сургалт'}</Title>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack maw={600}>
          <TextInput label="Гарчиг" required {...form.getInputProps('title')} />
          <Textarea label="Тайлбар" autosize minRows={4} {...form.getInputProps('description')} />
          <Select label="Багш" data={teachers} required searchable {...form.getInputProps('teacherId')} />

          <div>
            <Text size="sm" fw={500} mb={6}>Үнийн төрөл</Text>
            <SegmentedControl
              fullWidth
              data={[
                { value: 'free', label: 'Үнэгүй' },
                { value: 'premium', label: 'Премиум' },
                { value: 'fixed', label: 'Тогтмол үнэ' },
              ]}
              {...form.getInputProps('priceType')}
            />
          </div>

          {priceType === 'fixed' && (
            <NumberInput
              label="Үнэ (₮)"
              min={0}
              thousandSeparator=","
              {...form.getInputProps('price')}
            />
          )}

          {priceType === 'premium' && (
            <Select
              label="Премиум хугацаа"
              data={premiumDurationOptions}
              description="Хэрэглэгч хэр удаан премиум эрхтэй байвал үзэх боломжтой вэ"
              {...form.getInputProps('premiumDuration')}
            />
          )}

          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <NumberInput label="Нийт цаг" min={0} {...form.getInputProps('totalHours')} />
            <Select label="Ангилал" data={categories} {...form.getInputProps('category')} />
          </SimpleGrid>

          <Select label="Статус" data={statuses} {...form.getInputProps('status')} />
          <Switch label="Онцлох" {...form.getInputProps('isFeatured', { type: 'checkbox' })} />
          <Switch label="Зөвхөн гишүүн" {...form.getInputProps('isMemberOnly', { type: 'checkbox' })} />
          <div>
            <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, display: 'block' }}>Зураг</label>
            <ImageUpload value={form.values.thumbnailUrl} onChange={(url) => form.setFieldValue('thumbnailUrl', url)} />
          </div>
          <Group>
            <Button type="submit" loading={loading}>{id ? 'Хадгалах' : 'Үүсгэх'}</Button>
            <Button variant="default" onClick={() => navigate('/courses')}>Буцах</Button>
          </Group>
        </Stack>
      </form>
    </div>
  )
}
